import { createContext, FunctionComponent, useContext, useEffect, useState } from "react";
import { GroupConverter } from "../../domain/converter/group.converter";
import { IGroup } from "../../domain/group/group";
import { useGetAgencyCompaniesQuery } from "../../repositories/queries/get-agency-companies.query";
import { useAuthContext } from "../../providers/Auth/auth.provider";

interface IPomAgencyProvider {
  agencyGroups: IGroup[];
  isLoading: boolean;
  setNewErpQuery: (erpQuery: string) => void;
  setNewNameQuery: (nameQuery: string) => void;
  page: number;
  setNewPage: (page: number) => void;
  pageSize: number;
  setNewPageSize: (page: number) => void;
  curErpQuery: string | undefined;
  curNameQuery: string | undefined;
  itemCount: number;
}

export const PomAgencyContext = createContext<IPomAgencyProvider>({} as IPomAgencyProvider);

export const usePomAgencyContext = () => {
  return useContext(PomAgencyContext);
};

const usePomAgencyProvider = (): IPomAgencyProvider => {
  const [agencyGroups, setAgencyGroups] = useState<IGroup[]>([]);
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(50);
  const [itemCount, setItemCount] = useState<number>(0);
  const [curErpQuery, setCurErpQuery] = useState<string | undefined>();
  const [curNameQuery, setCurNameQuery] = useState<string | undefined>();
  const { internalUser } = useAuthContext();

  const { data: agencyCompanies, isLoading } = useGetAgencyCompaniesQuery(internalUser?.group?.erpNumber, page, pageSize, curErpQuery, curNameQuery);

  useEffect(() => {
    if (!agencyCompanies) return;
    const groups = agencyCompanies.items;
    const convertedGroups = groups.map((group) => GroupConverter.toDomain(group));
    setItemCount(agencyCompanies.meta.numberOfRecords);
    setAgencyGroups(convertedGroups);
  }, [agencyCompanies]);

  const setNewErpQuery = (searchQuery: string): void => {
    // if search value changes, reset page
    if (searchQuery !== curErpQuery) {
      setPage(0);
    }

    setCurErpQuery(searchQuery);
  };

  const setNewNameQuery = (searchQuery: string): void => {
    // if search value changes, reset page
    if (searchQuery !== curNameQuery) {
      setPage(0);
    }

    setCurNameQuery(searchQuery);
  };

  const setNewPage = (newPage: number): void => {
    setPage(newPage);
  };

  const setNewPageSize = (newPageSize: number): void => {
    setPageSize(newPageSize);
    setPage(0);
  };

  return {
    agencyGroups,
    isLoading,
    setNewErpQuery,
    setNewNameQuery,
    page,
    setNewPage,
    pageSize,
    setNewPageSize,
    curErpQuery,
    curNameQuery,
    itemCount,
  };
};

export const PomAgencyProvider: FunctionComponent<React.PropsWithChildren> = (props) => {
  const value = usePomAgencyProvider();
  return <PomAgencyContext.Provider value={value}>{props.children}</PomAgencyContext.Provider>;
};
